import React from 'react';
import { useTranslation } from 'react-i18next';
import { ButtonBase } from '@mui/material';
import {
    RM3_BED,
    RM3_EXTRAS,
    RM3_FILTER,
    RM3_RIBBON,
    RM3_RIBBON_ORDER,
} from 'arbitrip-common/client/utils/HotelDealsRm3';

import { RM3_ACTION } from './rm3FiltersReducer';
import { RIBBON_I18N_SUFFIX } from './Rm3Ribbon';
import { IconReset, IconTopPick } from './Rm3Icons';

const EXTRAS_PILLS = [
    { value: RM3_EXTRAS.BREAKFAST, labelKey: 'HotelDealsRm3.FilterBreakfastIncluded', testid: 'rm3-filter-breakfast' },
    { value: RM3_EXTRAS.ROOM_ONLY, labelKey: 'HotelDealsRm3.FilterRoomOnly', testid: 'rm3-filter-room-only' },
];

const BED_PILLS = [
    { value: RM3_BED.KING, labelKey: 'HotelDealsRm3.FilterKingBed', testid: 'rm3-filter-king' },
    { value: RM3_BED.DOUBLE, labelKey: 'HotelDealsRm3.FilterDoubleBed', testid: 'rm3-filter-double' },
    { value: RM3_BED.TWIN, labelKey: 'HotelDealsRm3.FilterTwinBed', testid: 'rm3-filter-twin' },
];

function Pill({ on, onClick, testid, className = '', children }) {
    return (
        <ButtonBase
            className={`rm3-pill${on ? ' rm3-pill--on' : ''} ${className}`}
            onClick={onClick}
            aria-pressed={on}
            data-testid={testid}
            disableRipple
        >
            {children}
        </ButtonBase>
    );
}

/**
 * Desktop filter pills (spec §1.2, §3.5): top picks (multi-select OR, only kinds present on the page), then
 * single-select groups (click again clears), the In policy / Pay at hotel toggles under today's visibility
 * conditions, and Reset filters (filters only, not sort) when any filter is on.
 */
function Rm3FilterBar({ state, dispatch, topPicks, available, showInPolicy, showPayAtHotel, anyFilterOn }) {
    const { t } = useTranslation();
    const { filters } = state;
    // A chip that can match nothing is not offered; an active chip always stays so it can be turned off.
    const freeCancellationOn = filters[RM3_FILTER.FREE_CANCELLATION];
    const inPolicyOn = filters[RM3_FILTER.IN_POLICY];
    const payAtHotelOn = filters[RM3_FILTER.PAY_AT_HOTEL];
    const activeTopPicks = filters[RM3_FILTER.TOP_PICKS];
    const topPickPills = RM3_RIBBON_ORDER.filter((kind) => topPicks.includes(kind) || activeTopPicks.includes(kind));
    const extrasPills = EXTRAS_PILLS.filter(
        (pill) => filters[RM3_FILTER.EXTRAS] === pill.value || available[RM3_FILTER.EXTRAS][pill.value],
    );
    const bedPills = BED_PILLS.filter(
        (pill) => filters[RM3_FILTER.BED] === pill.value || available[RM3_FILTER.BED][pill.value],
    );

    return (
        <div className="rm3-filter-bar" data-testid="rm3-filter-bar">
            {topPickPills.map((kind) => (
                <Pill
                    key={kind}
                    on={activeTopPicks.includes(kind)}
                    onClick={() => dispatch({ type: RM3_ACTION.TOGGLE_TOP_PICK, value: kind })}
                    testid={`rm3-top-pick-${kind}`}
                    className="rm3-pill--top-pick"
                >
                    <span className={`rm3-pill__icon ${kind === RM3_RIBBON.LOW ? 'rm3-pill__icon--low' : ''}`}>
                        <IconTopPick kind={kind} />
                    </span>
                    {t(`HotelDealsRm3.TopPick.${RIBBON_I18N_SUFFIX[kind]}`)}
                </Pill>
            ))}
            {(freeCancellationOn || available[RM3_FILTER.FREE_CANCELLATION]) && (
                <Pill
                    on={freeCancellationOn}
                    onClick={() => dispatch({ type: RM3_ACTION.TOGGLE_FREE_CANCELLATION })}
                    testid="rm3-filter-free-cancellation"
                >
                    {t('HotelDealsRm3.FilterFreeCancellation')}
                </Pill>
            )}
            {extrasPills.map((pill) => (
                <Pill
                    key={pill.value}
                    on={filters[RM3_FILTER.EXTRAS] === pill.value}
                    onClick={() => dispatch({ type: RM3_ACTION.SELECT_EXTRAS, value: pill.value })}
                    testid={pill.testid}
                >
                    {t(pill.labelKey)}
                </Pill>
            ))}
            {bedPills.map((pill) => (
                <Pill
                    key={pill.value}
                    on={filters[RM3_FILTER.BED] === pill.value}
                    onClick={() => dispatch({ type: RM3_ACTION.SELECT_BED, value: pill.value })}
                    testid={pill.testid}
                >
                    {t(pill.labelKey)}
                </Pill>
            ))}
            {(inPolicyOn || (showInPolicy && available[RM3_FILTER.IN_POLICY])) && (
                <Pill
                    on={inPolicyOn}
                    onClick={() => dispatch({ type: RM3_ACTION.TOGGLE_IN_POLICY })}
                    testid="rm3-filter-in-policy"
                >
                    {t('HotelDealsRm3.FilterInPolicy')}
                </Pill>
            )}
            {(payAtHotelOn || (showPayAtHotel && available[RM3_FILTER.PAY_AT_HOTEL])) && (
                <Pill
                    on={payAtHotelOn}
                    onClick={() => dispatch({ type: RM3_ACTION.TOGGLE_PAY_AT_HOTEL })}
                    testid="rm3-filter-pay-at-hotel"
                >
                    {t('HotelDealsRm3.FilterPayAtHotel')}
                </Pill>
            )}
            {anyFilterOn && (
                <ButtonBase
                    className="rm3-reset"
                    onClick={() => dispatch({ type: RM3_ACTION.RESET_FILTERS })}
                    data-testid="rm3-reset-filters"
                    disableRipple
                >
                    {t('HotelDealsRm3.ResetFilters')}
                    <IconReset />
                </ButtonBase>
            )}
        </div>
    );
}

export default Rm3FilterBar;
